import { ChevronRight, ExternalLink } from "lucide-react";
import { type CSSProperties, useEffect, useState } from "react";
import { sectionPreloader } from "../../lib/section-preload";
import { APP_NAME, APP_NAME_LINES, AUTHORITY_SHORT, COUNTRY } from "../../lib/tenant";
import { THEME_SWITCHING_ENABLED, type Theme, ThemeToggle } from "../../lib/theme";
import { useMediaQuery } from "../../lib/use-breakpoint";
import { TRYSIDE_CSS } from "./shell-css";
import { C, FU, NAV_SEL_EDGE, NAV_SEL_FILL, TREE_W, TREE_W_COLLAPSED, accentInk } from "./tokens";
import { WorkspaceSwitcher } from "./workspace-switcher";
import type { WorkspaceId } from "./workspaces";

/* ── The sidebar as a self-contained dark "observatory" island ───────────────
   The Intelligence sections render on the always-dark IntelShell canvas
   (DS_SHELL_TOKENS force dark values regardless of the app theme). A light
   sidebar beside that dark canvas read as a seam, so the rail adopts the same
   navy palette — a dark island exactly like DsShell, with the app root left on
   its own theme. Scoped onto `.tryside` as inline custom-property overrides so
   every child that reads C.* / --tq-mute / --color-accent (the workspace
   switcher, search, scope control, theme toggle, the tree itself) re-themes at
   once. Blue #82BAFF — the shell's kicker / arrivals identity hue — is the
   nav-wide accent (NOT the shell's green CTA accent: the sidebar has no CTAs). */
/* Light mode isn't prepped yet, so the user-facing toggle is hidden to avoid a
   false affordance. The switch — and the full rationale — now lives in
   lib/theme.tsx as THEME_SWITCHING_ENABLED, which ALSO hard-locks the theme
   resolver to dark (ignoring any stale "light" in localStorage). Flip that one
   constant to revive the toggle and honour stored choices. */

/* The rail's light/dark token SET now lives in CSS (.tryside +
   [data-theme="light"] .tryside in the style block below), so it always tracks
   the data-theme cascade and can never lag behind it on a re-render — the old
   inline, prop-derived token set could, showing near-white ink on a light rail
   until the next click. The only theme-dependent values still emitted inline are
   the per-workspace accent pair, and those are provided for BOTH themes at once
   (--nav-accent-dk / --nav-accent-lt); the CSS above routes the right one per
   theme. Defaults (#82BAFF dark / #2b5fb0 light) live in the CSS var fallbacks. */

/* Per-workspace nav accent — the left-tree active-item hue matches the
   workspace's identity colour (the same hue that owns the section eyebrow, the
   top-nav rail and the italic title word), rather than a single global blue.
   Only Prioritization is overridden for now; the rest keep the Intelligence
   blue until they're audited onto their own hues. */
const NAV_ACCENT: Partial<Record<WorkspaceId, { accent: string; hover: string }>> = {
  prioritization: { accent: "#A89BFF", hover: "#b6acff" },
  // Segmentation owns the green identity hue (WORKSPACE_ACCENT.segmentation),
  // applied to the left-tree active item so the sidebar matches the section
  // eyebrows, top-nav rail and italic title word inside the workspace. Its
  // DATA-viz hue is magenta (see metric-map segDarkAt) — chrome is green.
  segmentation: { accent: "#5DD4A4", hover: "#6fe0b3" },
  // Demand Signals owns the coral/orange identity hue (WORKSPACE_ACCENT), applied
  // to the left-tree active item so the sidebar matches the section eyebrows,
  // top-nav rail and italic title word inside the workspace.
  "demand-signals": { accent: "#FF8A5C", hover: "#ff9d75" },
  // Go to Market — the brand gold #FFC02E (WORKSPACE_ACCENT.gtm), matching the
  // section eyebrows, step rail and italic title word now that the GTM shells
  // pin --color-amber-text to brand gold (design feedback 2026-07-24).
  gtm: { accent: "#FFC02E", hover: "#ffce5c" },
  // Product Matching — the pink #FF6B9D (WORKSPACE_ACCENT.product), matching the
  // 1.1 section's mono eyebrow, step rail and italic title word (--gtp-hue).
  product: { accent: "#FF6B9D", hover: "#ff85af" },
};

/* Emits the workspace accent for BOTH themes as inline vars; the CSS routes the
   right one per data-theme (so it never desyncs). No override → the vars are
   omitted and the CSS fallbacks (#82BAFF dark / #2b5fb0 light) apply. On light
   the bright hue is darkened toward navy (accentInk) to match the section
   eyebrows / italic title word; dark keeps the raw hue — byte-identical. */
function navAccentVars(workspace: WorkspaceId): CSSProperties {
  const hue = NAV_ACCENT[workspace];
  if (!hue) return {};
  return {
    "--nav-accent-dk": hue.accent,
    "--nav-accent-hover-dk": hue.hover,
    "--nav-accent-lt": accentInk(hue.accent, true),
    "--nav-accent-hover-lt": accentInk(hue.hover, true),
  } as CSSProperties;
}

/**
 * Collapsible left-rail section tree for the /try sandbox.
 *
 * Mirrors the canonical sidebar IA in [pages/index.tsx] but adds a desktop
 * collapse mode — the canonical only collapses for mobile (<900px drawer).
 *
 * Two states:
 *   expanded (~260px) — Notion-style tree with group headers and items
 *   collapsed (56px)  — icon rail with the 5 group numbers stacked vertically
 *
 * Clicking a group number while collapsed expands the rail with that group
 * scrolled into view. State persists in localStorage so the user's pick
 * sticks across reloads. Mobile (<900px) still drops to a drawer, same as
 * the canonical.
 */

export interface SectionItem {
  id: string;
  n: string;
  label: string;
  /** External destination — the item renders as a link opening in a new tab
      (with an external-link icon) instead of activating an in-page section. */
  href?: string;
}

export interface SectionGroup {
  id: string;
  n: string;
  label: string;
  items: SectionItem[];
  /**
   * Render the items directly, with no collapsible group header.
   *
   * Used by workspaces whose scope switcher already does the grouping — Demand
   * Signals splits Global/Saudi on the scope control above, so a lone "01 Global
   * live demand signals" header beneath it would just restate the pill. The
   * group's `n`/`label` are still required (breadcrumbs and the pager read
   * them); they simply aren't drawn in the rail.
   */
  flat?: boolean;
}

type View = "global" | "saudi";

interface Props {
  /** Groups for the active workspace, already resolved (incl. scope) by the parent. */
  groups: SectionGroup[];
  /** Active workspace — drives the switcher dropdown at the top of the sidebar. */
  workspace: WorkspaceId;
  /** Switch to another workspace. */
  onChangeWorkspace: (id: WorkspaceId) => void;
  /** Return to the first-run landing hub. */
  onHome: () => void;
  /** Whether to show the Global/Saudi scope switcher (Intelligence only). */
  showScope: boolean;
  activeId: string;
  onPick: (id: string) => void;
  view: View;
  onChangeView: (next: View) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Mobile (≤900px) off-canvas drawer state. At these widths the rail is a
      slide-in panel toggled by the shell's hamburger; ignored on desktop. */
  drawerOpen: boolean;
  /** Close the mobile drawer — scrim tap, the in-drawer ✕, or after a pick. */
  onCloseDrawer: () => void;
  /** Theme is owned by the page; the sidebar footer hosts the toggle so the
      top of the page stays uncluttered. */
  theme: Theme;
  setTheme: (next: Theme) => void;
}

export function SectionTree({
  groups,
  workspace,
  onChangeWorkspace,
  onHome,
  showScope,
  activeId,
  onPick,
  view,
  onChangeView,
  collapsed,
  onToggleCollapsed,
  drawerOpen,
  onCloseDrawer,
  theme,
  setTheme,
}: Props) {
  const currentGroups = groups;

  // At ≤900px the rail is an off-canvas drawer, and a drawer that showed the
  // icon-only rail would be useless — a reader can't tell that "1.2" is Inbound
  // from a number alone. So on mobile we force the FULL labelled layout,
  // ignoring the persisted desktop collapsed flag. `collapsed` drives the JSX
  // (mono "TQ" vs wordmark, icon tree vs labelled tree), so this has to be a
  // real value, not just a CSS override. 900px matches the shell's mobile
  // cutoff (dashboard.tsx), where the sidebar hands off to the drawer.
  const isMobileRail = useMediaQuery("(max-width: 900px)");
  const railCollapsed = isMobileRail ? false : collapsed;

  // Which group contains the active sub-item? That's the only group we
  // expand by default — "focus current group" mode. The user can still
  // open siblings manually (they're one click away), but they aren't on
  // screen as ambient noise.
  const activeGroupId =
    currentGroups.find((g) => g.items.some((it) => it.id === activeId))?.id ?? currentGroups[0]?.id ?? "";

  // Expansion state is intentionally NOT persisted across navigations. Every
  // time the user lands on a new section, the tree resets to focus mode —
  // matches the "by default, collapsed" instruction from the design review.
  // Manual toggles within a session still work; they're just session-local.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([activeGroupId]));

  // Re-focus when the active group changes (cross-group navigation) or when
  // the scope flips (Global ↔ Saudi). Same-group nav (e.g. 2.1 → 2.2) keeps
  // the current expansion so we don't snap shut and reopen. `view` is an
  // intentional re-run trigger for the scope flip, even when activeGroupId
  // is unchanged.
  // biome-ignore lint/correctness/useExhaustiveDependencies: view triggers re-focus on scope flip
  useEffect(() => {
    setExpanded((prev) => {
      if (prev.has(activeGroupId)) return prev;
      return new Set([activeGroupId]);
    });
  }, [activeGroupId, view]);

  const toggleGroup = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // One tree row. Shared by the grouped and flat renderers below so a section
  // looks and behaves identically whether or not it sits under a group header.
  const renderItem = (it: SectionItem) => {
    if (it.href) {
      return (
        <li key={it.id}>
          <a
            className="tryside-item"
            href={it.href}
            target="_blank"
            rel="noreferrer"
            title={`${it.label} - opens in a new tab`}
          >
            <span className="tryside-item-n">{it.n}</span>
            <span className="tryside-item-label">{it.label}</span>
            <ExternalLink size={13} strokeWidth={2} className="tryside-item-ext" aria-hidden="true" />
          </a>
        </li>
      );
    }
    return (
      <li key={it.id}>
        <button
          type="button"
          className="tryside-item"
          data-active={it.id === activeId}
          onClick={() => onPick(it.id)}
          onPointerEnter={() => sectionPreloader.now(it.id)}
          onFocus={() => sectionPreloader.now(it.id)}
        >
          <span className="tryside-item-n">{it.n}</span>
          <span className="tryside-item-label">{it.label}</span>
        </button>
      </li>
    );
  };

  return (
    <aside
      className="tryside"
      data-collapsed={railCollapsed}
      data-drawer-open={drawerOpen}
      aria-label="Primary navigation"
      style={navAccentVars(workspace)}
    >
      {/* ── Brand (Threnvik Qalmoth wordmark) + collapse toggle ──────────────────
          The wordmark anchors the sidebar in the conventional top-left logo
          slot. The workspace switcher moved to its own full-width row below
          (see .tryside-workspace) so it reads as navigation, not the brand. */}
      <div className="tryside-brand">
        {railCollapsed ? (
          <span className="tryside-logo-mono" title={APP_NAME}>
            {AUTHORITY_SHORT}
          </span>
        ) : (
          <div className="tryside-logo">
            <span className="tryside-logo-mark">{APP_NAME_LINES[0]}</span>
            <span className="tryside-logo-sub">{APP_NAME_LINES[1]}</span>
          </div>
        )}
        <button
          type="button"
          className="tryside-collapse"
          onClick={onToggleCollapsed}
          aria-label={railCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={railCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {/* Panel-toggle icon — rectangle with a left-rail divider. Direction-
              agnostic (the icon represents the sidebar itself, not a chevron),
              so we don't rotate it when state flips. Same convention as
              Linear, Notion, VS Code. */}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="2.5" y="3.5" width="11" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
            <line x1="6" y1="3.5" x2="6" y2="12.5" stroke="currentColor" strokeWidth="1.4" />
          </svg>
        </button>
        {/* Mobile-only drawer close (✕) — swaps in for the desktop collapse
            toggle at ≤900px (the collapse action is meaningless in a drawer). */}
        <button
          type="button"
          className="tryside-drawer-close"
          onClick={onCloseDrawer}
          aria-label="Close navigation"
          title="Close navigation"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <line x1="4.5" y1="4.5" x2="13.5" y2="13.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <line x1="13.5" y1="4.5" x2="4.5" y2="13.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* ── Workspace switcher — its own full-width row under the brand, in
           BOTH states (icon-only when collapsed). Keeping it in a fixed slot
           (rather than stacked inside the collapsed brand) means the brand
           stays 56px tall in both states, so the switcher/search/tree below
           don't jump vertically when toggling. ───────────────────────────── */}
      <div className="tryside-workspace">
        <WorkspaceSwitcher active={workspace} collapsed={railCollapsed} onPick={onChangeWorkspace} onHome={onHome} />
      </div>

      {/* ── Search ──────────────────────────────────────────────────────────
          Visual stub mirroring the canonical's Cmd-K pattern. Functional
          search lands in a follow-up plan. When collapsed, the input + kbd
          hint hide; the icon alone becomes a click-to-expand affordance. */}
      {railCollapsed ? (
        <button
          type="button"
          className="tryside-search tryside-search-collapsed"
          onClick={onToggleCollapsed}
          aria-label="Open search"
          title="Search sections"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <line x1="9.5" y1="9.5" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
        <div className="tryside-search">
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <line x1="9.5" y1="9.5" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            placeholder="Search sections…"
            className="tryside-search-input"
            aria-label="Search sections"
          />
          <span className="tryside-search-kbd">⌘K</span>
        </div>
      )}

      {/* ── Scope switcher — Intelligence only (Global/Saudi), hidden when
           collapsed. Other peers (Segmentation, Prioritization, GTM) are not
           scoped by geography. ──────────────────────────────────────────── */}
      {showScope && !railCollapsed && (
        <div className="tryside-scope" role="tablist" aria-label="Scope">
          <button
            type="button"
            role="tab"
            aria-selected={view === "global"}
            data-active={view === "global"}
            onClick={() => onChangeView("global")}
            className="tryside-scope-btn"
          >
            Global
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "saudi"}
            data-active={view === "saudi"}
            onClick={() => onChangeView("saudi")}
            className="tryside-scope-btn"
          >
            {COUNTRY}
          </button>
        </div>
      )}

      {/* ── Tree / rail body ─────────────────────────────────────────────── */}
      <nav className="tryside-body" aria-label="Sections">
        {railCollapsed
          ? // Collapsed rail — just the group numbers, stacked. Clicking a
            // number expands the sidebar so the user lands inside that group.
            currentGroups.map((g) => {
              // A flat group has no header to open, so the rail drops to the
              // section numbers themselves and picking one navigates directly
              // (no expand round-trip).
              if (g.flat) {
                return g.items
                  .filter((it) => !it.href)
                  .map((it) => (
                    <button
                      type="button"
                      key={it.id}
                      className="tryside-rail-num"
                      data-active={it.id === activeId}
                      title={it.label}
                      aria-label={`${it.n} ${it.label}`}
                      onClick={() => onPick(it.id)}
                      onPointerEnter={() => sectionPreloader.now(it.id)}
                      onFocus={() => sectionPreloader.now(it.id)}
                    >
                      {it.n}
                    </button>
                  ));
              }
              const isCurrentGroup = g.items.some((it) => it.id === activeId);
              return (
                <button
                  type="button"
                  key={g.id}
                  className="tryside-rail-num"
                  data-active={isCurrentGroup}
                  title={g.label}
                  aria-label={`${g.n} ${g.label} - expand sidebar`}
                  onClick={() => {
                    onToggleCollapsed();
                    // Re-open the group so the user lands directly on its
                    // items. If the group was previously collapsed manually,
                    // this re-opens it — gentler than respecting their old
                    // pick when they explicitly asked to see this group.
                    setExpanded((prev) => new Set(prev).add(g.id));
                  }}
                >
                  {g.n}
                </button>
              );
            })
          : currentGroups.map((g) => {
              // Flat group — items only, no header, never collapsible.
              if (g.flat) {
                return (
                  <ul key={g.id} className="tryside-items" data-flat="true" aria-label={g.label}>
                    {g.items.map(renderItem)}
                  </ul>
                );
              }
              const isOpen = expanded.has(g.id);
              return (
                <div key={g.id} className="tryside-group">
                  <button
                    type="button"
                    className="tryside-group-head"
                    aria-expanded={isOpen}
                    onClick={() => toggleGroup(g.id)}
                  >
                    <span className="tryside-group-n">{g.n}</span>
                    <span className="tryside-group-label">{g.label}</span>
                    <ChevronRight
                      size={13}
                      strokeWidth={2}
                      className="tryside-group-chev"
                      data-open={isOpen}
                      aria-hidden="true"
                    />
                  </button>
                  {isOpen && <ul className="tryside-items">{g.items.map(renderItem)}</ul>}
                </div>
              );
            })}
      </nav>

      {/* ── Footer — the theme toggle lives here but is hidden while light mode
           is unfinished (see THEME_SWITCHING_ENABLED). Kept wired for revival. */}
      {THEME_SWITCHING_ENABLED && (
        <div className="tryside-foot">
          <ThemeToggle theme={theme} setTheme={setTheme} variant="dark-surface" />
        </div>
      )}

      <style>{TRYSIDE_CSS}</style>
    </aside>
  );
}
